import React, { FC } from 'react';
import { AgentActivityResponse } from 'Rewards/services/endpoints/GetAgentActivity/types';
import styles from './styles.module.css';

// ── Date formatting ────────────────────────────────────────────────────────────
//
// occurredAt is an ISO-8601 UTC timestamp from the server (e.g.
// "2026-05-15T23:45:00Z"). Constructing a Date and calling toLocaleDateString()
// without a timezone would render in the browser's local offset — a 11:45 PM UTC
// activity would appear the *next* day for a user in UTC+1 or later.
//
// Instead we parse the date fields directly from the UTC timestamp so the
// displayed date always matches the calendar day the event was recorded in UTC.
// This matches the server's intent (it stores events at their wall-clock UTC time).

export function formatOccurredAt(occurredAt: string): string {
  // Parse the UTC date components directly to avoid timezone shift.
  const date = new Date(occurredAt);
  const month = date.getUTCMonth() + 1; // 0-indexed
  const day = date.getUTCDate();
  const year = String(date.getUTCFullYear()).slice(-2); // 2-digit year, consistent with formatPhaseDate
  return `${month}/${day}/${year}`;
}

// ── Phase date range formatting ────────────────────────────────────────────────
//
// phase.start / phase.end arrive as ISO-8601 UTC timestamps from the server
// (e.g. "2026-05-01T00:00:00Z") — the server serializes the phase Instants.
// Parse the UTC date components (same approach as formatOccurredAt) so the
// displayed boundary matches the UTC wall-clock date. A naive split on "-"
// would leave the day as "01T00:00:00Z" → NaN.

export function formatPhaseDate(dateStr: string): string {
  const date = new Date(dateStr);
  const month = date.getUTCMonth() + 1; // 0-indexed
  const day = date.getUTCDate();
  const year = String(date.getUTCFullYear()).slice(-2); // 2-digit year
  return `${month}/${day}/${year}`;
}

// ── Props ──────────────────────────────────────────────────────────────────────

type RewardActivityProps = {
  loading: boolean;
  data: AgentActivityResponse | null | undefined;
  error?: unknown;
  /** Re-runs the activity fetch when the user taps "Try again" in the error state. */
  onRetry?: () => void;
};

// ── Component ──────────────────────────────────────────────────────────────────

export const RewardActivity: FC<RewardActivityProps> = ({
  loading,
  data,
  error,
  onRetry,
}) => {
  // Loading state — render nothing until data resolves to avoid flicker (fe-loading-states.md).
  if (loading) {
    return null;
  }

  // Not-yet-fetched / skipped query — render nothing, not an error. The activity
  // hook is lazy in mock mode and for non-producers (lazy = rewardsMockEnabled ||
  // !showProducerView), which leaves data === undefined with no error. Treating
  // that as an error would flash the error panel for mock-mode producers, so only
  // an explicit error or a malformed 2xx body (handled below) is an error state.
  if (data == null && !error) {
    return null;
  }

  // Error / malformed-payload state — keep the section visible with an explicit
  // retry instead of silently vanishing. A transient backend error (e.g. a flaky
  // /activity fetch) must not look like the agent's reward history was lost.
  // Guarding the shape here also avoids a crash on the `{ phase, rows }`
  // destructure below if a 2xx body arrives without those fields.
  const isUsable =
    !error && data != null && data.phase != null && Array.isArray(data.rows);
  if (!isUsable) {
    return (
      <section className={styles.section} data-testid="reward-activity-section">
        <div className={styles.header}>
          <h2 className={styles.title} data-testid="reward-activity-title">
            Reward Activity
          </h2>
        </div>
        <div className={styles.errorState} data-testid="reward-activity-error">
          <p>We couldn&apos;t load your reward activity right now.</p>
          {onRetry && (
            <button
              type="button"
              className={styles.retryButton}
              onClick={onRetry}
              data-testid="reward-activity-retry"
            >
              Try again
            </button>
          )}
        </div>
      </section>
    );
  }

  const { phase, rows } = data;

  // Header subtitle: "Results for M/D/YY – M/D/YY"
  const dateRangeSubtitle = `Results for ${formatPhaseDate(phase.start)} – ${formatPhaseDate(phase.end)}`;

  return (
    <section className={styles.section} data-testid="reward-activity-section">
      <div className={styles.header}>
        <h2 className={styles.title} data-testid="reward-activity-title">
          Reward Activity
        </h2>
        <p className={styles.subtitle} data-testid="reward-activity-subtitle">
          {dateRangeSubtitle}
        </p>
      </div>

      {rows.length === 0 ? (
        <div
          className={styles.emptyState}
          data-testid="reward-activity-empty-state"
        >
          {/* TODO(DLC-459): confirm empty-state copy with design */}
          No reward activity yet for this period.
        </div>
      ) : (
        <table className={styles.table} data-testid="reward-activity-table">
          <thead className={styles.tableHead}>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Activity</th>
              <th>Reward</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {rows.map((row) => (
              <tr
                key={row.sequence}
                data-testid={`reward-activity-row-${row.sequence}`}
              >
                <td>{row.sequence}</td>
                <td>{formatOccurredAt(row.occurredAt)}</td>
                <td>{row.activityLabel}</td>
                <td>{row.reward.text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
